/**
 * As ocorrências de custódia do caso (E136.F3, GUS-152, ADR-0092): cada arquivo que divergiu do
 * registro do recebimento ou sumiu, com os dois hashes, quando e por qual leitura apareceu e, se
 * resolvida, como, por quem e quando. As abertas vêm primeiro, com a ação "Resolver".
 *
 * Enquanto a ocorrência não é resolvida com o restauro, o arquivo e o que depende dele ficam fora do
 * exame; os demais documentos seguem. Resolver não apaga a ocorrência.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Badge, Button, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { OcorrenciaDaCustodiaData } from '@/lib/custodia';
import { formatarDataHora } from '@/lib/trilha_format';

import { CartaoDaAbertura } from './PecasDaAbertura';
import { ResolverOcorrencia } from './ResolverOcorrencia';

const t = ptBR.abertura.custodia.ocorrencias;

function nomeDoObjeto(ocorrencia: OcorrenciaDaCustodiaData): string {
  return ocorrencia.nome ?? t.semNome[ocorrencia.especie];
}

function linhaDaAbertura(ocorrencia: OcorrenciaDaCustodiaData): string {
  const linha = t.abertaEm
    .replace('{data}', formatarDataHora(ocorrencia.aberta_em))
    .replace('{origem}', t.origem[ocorrencia.origem]);
  return ocorrencia.aberta_por === null
    ? linha
    : `${linha} · ${t.abertaPor.replace('{quem}', ocorrencia.aberta_por)}`;
}

function Resolucao({ ocorrencia }: { ocorrencia: OcorrenciaDaCustodiaData }): JSX.Element | null {
  const resolucao = ocorrencia.resolucao;
  if (resolucao === null) return null;
  const linha = t.resolvidaEm
    .replace('{acao}', t.acao[resolucao.acao])
    .replace('{data}', formatarDataHora(resolucao.resolvida_em));
  const quem =
    resolucao.responsavel === null
      ? ''
      : ` ${t.resolvidaPor.replace('{quem}', resolucao.responsavel)}`;
  return (
    <div
      className="mt-sc-2 text-body-sm text-fg"
      data-testid={`ocorrencia-resolucao-${ocorrencia.id}`}
    >
      <p>{`${linha}${quem}`}</p>
      {resolucao.nota ? <p className="text-fg-muted">{resolucao.nota}</p> : null}
    </div>
  );
}

function Hashes({ ocorrencia }: { ocorrencia: OcorrenciaDaCustodiaData }): JSX.Element {
  return (
    <div className="mt-sc-2 space-y-1 text-caption text-fg-dim">
      {ocorrencia.esperado ? (
        <p>
          {t.esperado} <MonoData>{ocorrencia.esperado}</MonoData>
        </p>
      ) : null}
      {ocorrencia.obtido ? (
        <p>
          {t.obtido} <MonoData>{ocorrencia.obtido}</MonoData>
        </p>
      ) : null}
    </div>
  );
}

interface ItemProps {
  client: AxiosInstance;
  caseId: string;
  ocorrencia: OcorrenciaDaCustodiaData;
  onResolvido: () => Promise<void>;
  somenteLeitura: boolean;
}

function ItemDaOcorrencia({
  client,
  caseId,
  ocorrencia,
  onResolvido,
  somenteLeitura,
}: ItemProps): JSX.Element {
  const [resolvendo, setResolvendo] = useState(false);
  const aberta = ocorrencia.resolucao === null;
  return (
    <li
      className="border-b border-border py-sc-3 last:border-b-0"
      data-testid={`ocorrencia-${ocorrencia.id}`}
    >
      <div className="flex flex-wrap items-center gap-sc-2">
        <span className="text-body-sm font-semibold text-fg">{nomeDoObjeto(ocorrencia)}</span>
        <Badge variant={aberta ? 'danger' : 'neutral'}>{aberta ? t.aberta : t.resolvida}</Badge>
      </div>
      <p className="text-body-sm text-fg">{t.situacao[ocorrencia.situacao]}</p>
      <p className="text-caption text-fg-muted">{linhaDaAbertura(ocorrencia)}</p>
      <Hashes ocorrencia={ocorrencia} />
      <Resolucao ocorrencia={ocorrencia} />
      {aberta && !resolvendo && !somenteLeitura ? (
        <Button
          type="button"
          variant="secondary"
          className="mt-sc-2"
          data-testid={`ocorrencia-abrir-${ocorrencia.id}`}
          onClick={() => setResolvendo(true)}
        >
          {t.resolver}
        </Button>
      ) : null}
      {aberta && resolvendo ? (
        <ResolverOcorrencia
          client={client}
          caseId={caseId}
          ocorrenciaId={ocorrencia.id}
          onResolvido={onResolvido}
          onCancelar={() => setResolvendo(false)}
        />
      ) : null}
    </li>
  );
}

export interface OcorrenciasDaCustodiaProps {
  client: AxiosInstance;
  caseId: string;
  ocorrencias: readonly OcorrenciaDaCustodiaData[];
  onResolvido: () => Promise<void>;
  /** E143.F2: na abertura pelo banco a ocorrência é resolvida pela equipe (sem a ação). */
  somenteLeitura?: boolean;
}

export function OcorrenciasDaCustodia({
  client,
  caseId,
  ocorrencias,
  onResolvido,
  somenteLeitura = false,
}: OcorrenciasDaCustodiaProps): JSX.Element {
  const ordenadas = [
    ...ocorrencias.filter((o) => o.resolucao === null),
    ...ocorrencias.filter((o) => o.resolucao !== null),
  ];
  return (
    <CartaoDaAbertura titulo={t.titulo} testId="custodia-ocorrencias">
      <p className="text-caption text-fg-muted">{t.explica}</p>
      {ordenadas.length === 0 ? (
        <p
          className="mt-sc-3 text-body-sm text-fg-muted"
          data-testid="custodia-ocorrencias-nenhuma"
        >
          {t.nenhuma}
        </p>
      ) : (
        <ul className="mt-sc-2">
          {ordenadas.map((ocorrencia) => (
            <ItemDaOcorrencia
              key={ocorrencia.id}
              client={client}
              caseId={caseId}
              ocorrencia={ocorrencia}
              onResolvido={onResolvido}
              somenteLeitura={somenteLeitura}
            />
          ))}
        </ul>
      )}
    </CartaoDaAbertura>
  );
}
